import cn from 'classnames';
import dayjs from 'dayjs';
import get from 'lodash/get';
import last from 'lodash/last';
import { useRouter } from 'next/router';
import { useState, useEffect } from 'react';

/* Components */
import globalStyles from '../../../styles/pages/index.module.scss';
import { getProductPriceHistory } from '../../api/Products';
import CategoriesAside from '../../components/CategoriesAside/CategoriesAside';
import CategoriesBanner from '../../components/CategoriesBanner/CategoriesBanner';
import CategoriesSelect from '../../components/CategoriesSelect/CategoriesSelect';
import CustomHead from '../../components/CustomHead';
import DeliveryBanner from '../../components/DeliveryBanner/DeliveryBanner';
import FavoritesAside from '../../components/FavoritesAside/FavoritesAside';
import Footer from '../../components/Footer/Footer';
import Header from '../../components/Header/Header';
import Navigation from '../../components/Navigation/Navigation';
import ProductDetails from '../../components/ProductDetails/ProductDetails';
/* API */
/* Controllers */
import CartStore from '../../store/cart';
import DeliveryMode from '../../store/deliveryMode';
import SortBy from '../../store/productsSort';
import { getProductInfo, viewItem } from '../../utils/analytics';
import BANNER_SORT_OPTIONS from '../../utils/bannerOptions';
import { getOrFetchFooterLinks } from '../../utils/footerLinks';
/* Styles */
import { getBreadcrumbStepProduct, createBreadcrumbJson, getBreadcrumbValidation } from '../../utils/getBreadcrumb';
import { getOrFetchCategories } from '../../utils/getCategories';
import { getOrFetchNavigationLinks } from '../../utils/navigationLinks';
import { getPageTitle, getCategoryName, getSubCategoryName, getBadgeText, productImageURL } from '../../utils/products';
import { serverSideFetchingFromFile } from '../../utils/server-side/products';
import { initServiceShopCollectionRelationship } from '../../utils/storeRelationShips';
import { getDisplayName, getDisplayDescription } from '../../utils/transformers';

import pageStyles from './products.module.scss';

export default function Products({
  categories,
  navigation,
  footerLinks,
  productSelected,
  productDetails,
  badgeText,
  imgSrc,
  productDetailsTitle,
  description,
  category,
}) {
  const [deliveryMode, setDeliveryMode] = useState('');
  const [showBySelection, setShowBySelection] = useState(BANNER_SORT_OPTIONS.catalog);
  const [isCartVisible, setIsCartVisible] = useState(false);
  const [isProductAvailable, setIsProductAvailable] = useState(true);
  const [productLowestPrice, setProductLowestPrice] = useState(null);

  const router = useRouter();
  const { slug } = router.query;

  const breadcrumbData = slug.map((value, index) => getBreadcrumbStepProduct(slug, index));
  const validatedBreadcrumbData = getBreadcrumbValidation(breadcrumbData);
  const breadcrumb = createBreadcrumbJson(validatedBreadcrumbData);

  const categoryName = getCategoryName(slug, categories);
  const subCategoryName = getSubCategoryName(slug, categories);
  const title = getPageTitle(slug, categories);

  useEffect(() => {
    const subscriptionDeliveryMode = DeliveryMode.subscribe(setDeliveryMode);
    const isCartVisible$ = CartStore.getIsVisble$().subscribe(setIsCartVisible);
    const sortBy$ = SortBy.subscribe(setShowBySelection);

    const serviceShopCollection$ = initServiceShopCollectionRelationship(productSelected, setIsProductAvailable);
    getHistory();
    viewItem(getProductInfo(productSelected));

    return () => {
      sortBy$.unsubscribe();
      isCartVisible$.unsubscribe();
      subscriptionDeliveryMode.unsubscribe();
      serviceShopCollection$.unsubscribe();
    };
  }, []);

  async function getHistory() {
    const today = dayjs();
    const oneMonthBefore = today.subtract(1, 'month');
    const { prices } = await getProductPriceHistory(
      productSelected._id,
      oneMonthBefore.format('YYYY-MM-DD'),
      today.format('YYYY-MM-DD')
    );
    const lowestPriceHistory = findLowestPriceEntry(prices);
    setProductLowestPrice(lowestPriceHistory);
  }

  function findLowestPriceEntry(entries) {
    if (!entries || entries.length === 0) {
      return null; // Return null if the array is empty or invalid
    }

    return entries.reduce((lowest, entry) => {
      return entry.price < lowest.price ? entry : lowest;
    });
  }

  return (
    <>
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumb) }} />
      <CustomHead title={title} key="title" />
      <div className={globalStyles.headerContainer}>
        <Header navigation={navigation} isEnabled />
      </div>

      <Navigation navigation={navigation} />
      <DeliveryBanner />

      <CategoriesSelect slug={slug} />

      <main
        className={cn(pageStyles.main, {
          [pageStyles.spaceOpenCart]: isCartVisible,
        })}
      >
        <div className={cn([pageStyles.mainLeft, pageStyles.listBanner])}>
          <div>
            <CategoriesBanner
              onChangeOrderBy={e => SortBy.update(e.target.value)}
              {...{
                showBySelection,
                numOfProducts: 0,
                categoryName,
                subCategoryName,
                slug,
                deliveryMode,
                hideOrderBy: true,
              }}
            />
          </div>

          <section className={pageStyles.mainContent}>
            <div className={pageStyles.hideOnSmallScreen}>
              <div className={cn([pageStyles.categoriesAsideProductDetails])}>
                <FavoritesAside />
                <CategoriesAside category={category} slug={slug} />
              </div>
            </div>
            <ProductDetails
              productDetails={productDetails}
              _product={productSelected}
              isProductAvailable={isProductAvailable}
              badgeText={badgeText}
              imgSrc={imgSrc}
              title={productDetailsTitle}
              description={description}
              productLowestPrice={productLowestPrice}
            />
          </section>
        </div>
      </main>
      <Footer {...{ footerLinks }} />
    </>
  );
}

export async function getStaticPaths() {
  const products = serverSideFetchingFromFile();

  const paths = products.map(product => ({
    params: { slug: product.slug.split('products/')[1].split('/') },
  }));

  return { paths, fallback: false };
}

export async function getStaticProps(context) {
  const { slug } = context.params;
  const productNameSku = last(slug);
  const splitProductNameSku = productNameSku.split('-');
  const sku = last(splitProductNameSku);

  const categories = await getOrFetchCategories();
  const { data: navigation } = await getOrFetchNavigationLinks();
  const footerLinks = await getOrFetchFooterLinks();

  const products = serverSideFetchingFromFile();
  const productSelected = products.find(product => Number(product.sku) === Number(sku));
  const productDetails = productSelected?.details || {};
  const badgeText = getBadgeText(productSelected);
  const imgUrl = get(productSelected, 'images.primary', '');
  const imgSrc = productImageURL(imgUrl, 'productTile');
  const productDetailsTitle = getDisplayName(productSelected);
  const description = getDisplayDescription(productSelected);

  const categoryId = productSelected?.category?.categoryTree?.root;
  const category = categories.find(_category => _category._id === categoryId);

  return {
    props: {
      categories,
      navigation,
      footerLinks,
      productSelected,
      productDetails,
      badgeText,
      imgSrc,
      productDetailsTitle,
      description,
      category,
    },
  };
}
